"use client";

import { type LucideIcon, X } from "lucide-react";
import type { PropsWithChildren } from "react";
import type { ClassNameValue } from "tailwind-merge";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@repo/shared/utils";
import { useNestedComponentWithRef } from "./hooks/useNestedComponent";

const chipVariants = cva(
  "rounded-full border flex flex-row items-center px-2 gap-x-2",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        tertiary: "bg-tertiary text-tertiary-foreground hover:bg-tertiary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "px-2 gap-x-2 py-1 text-sm",
        sm: "px-1 gap-x-1 py-0 text-sm",
        lg: "px-4 gap-x-3 py-2 text-base",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ChipProps
  extends PropsWithChildren,
    VariantProps<typeof chipVariants> {
  icon?: LucideIcon;
  title?: string;
  className?: ClassNameValue;
  onClick?: () => void;
  onDelete?: () => void;
}

export const Chip = ({
  icon,
  children,
  title,
  className,
  variant,
  size,
  onClick = undefined,
  onDelete = undefined,
}: ChipProps) => {
  const Icon = useNestedComponentWithRef(icon);
  return (
    <div
      className={cn(chipVariants({ variant, size, className }))}
      title={title}
    >
      {Icon && (
        <Icon size={size === "sm" ? "12" : size === "lg" ? "24" : "16"} />
      )}
      <button
        type="button"
        className={cn(
          onClick
            ? "hover:scale-105 duration-100 cursor-pointer"
            : "cursor-default"
        )}
        onClick={onClick}
      >
        {children}
      </button>
      {onDelete && (
        <button
          type="button"
          className="border rounded-full opacity-75 cursor-pointer"
          onClick={onDelete}
        >
          <X size={size === "sm" ? "12" : size === "lg" ? "24" : "16"} />
        </button>
      )}
    </div>
  );
};
